// Toptan göçme durumu: girdi değişince 300 ms debounce ile Web Worker'da hesaplanır; sonuç zustand deposunda
// (sekme, sonuç paneli ve çizim katmanı buradan okur). Worker yoksa (jsdom/test) senkron çağrı.
import { useEffect, useRef } from "react";
import { create } from "zustand";
import { analyzeGlobalStability, type GlobalStabilityAnalysis, type WallInput } from "@tasduvar/engine";
import type { GsRequest, GsResponse } from "./globalStability.worker";

export type GsStatus = "idle" | "running" | "done" | "error";
export interface GlobalStabilityState {
  status: GsStatus;
  result: GlobalStabilityAnalysis | null;
  error: string | null;
  wallId: string | null;
  set(patch: Partial<Omit<GlobalStabilityState, "set">>): void;
}
export const useGlobalStabilityStore = create<GlobalStabilityState>((set) => ({ status: "idle", result: null, error: null, wallId: null, set: (patch) => set(patch) }));

export const GS_DEBOUNCE_MS = 300;
/** Testlerde debounce'u sıfırlamak için */
export const gsTiming = { debounceMs: GS_DEBOUNCE_MS };

function runSync(wall: WallInput): { result: GlobalStabilityAnalysis | null; error: string | null } {
  try { return { result: analyzeGlobalStability(wall), error: null }; }
  catch (e) { return { result: null, error: e instanceof Error ? e.message : "Toptan göçme hesabı yapılamadı" }; }
}

/** Seçili duvar için toptan göçme hesabını yönetir; depo durumunu döner. */
export function useGlobalStability(wall: WallInput | null): Omit<GlobalStabilityState, "set"> {
  const state = useGlobalStabilityStore();
  const workerRef = useRef<Worker | null>(null);
  const seq = useRef(0);
  const enabled = !!wall?.globalStability?.enabled;

  useEffect(() => () => { workerRef.current?.terminate(); workerRef.current = null; }, []);

  useEffect(() => {
    const set = useGlobalStabilityStore.getState().set;
    if (!wall || !enabled) { seq.current++; set({ status: "idle", result: null, error: null, wallId: wall?.id ?? null }); return; }
    const id = ++seq.current;
    set({ status: "running", wallId: wall.id });
    const timer = setTimeout(() => {
      if (id !== seq.current) return;
      if (typeof Worker === "undefined") { const r = runSync(wall); if (id === seq.current) set({ status: r.error ? "error" : "done", ...r, wallId: wall.id }); return; }
      if (!workerRef.current) {
        const w = new Worker(new URL("./globalStability.worker.ts", import.meta.url), { type: "module" });
        w.onmessage = (e: MessageEvent<GsResponse>) => {
          if (e.data.id !== seq.current) return;
          if ("error" in e.data) set({ status: "error", result: null, error: e.data.error });
          else set({ status: "done", result: e.data.result, error: null });
        };
        w.onerror = () => { const r = runSync(wall); set({ status: r.error ? "error" : "done", ...r }); };
        workerRef.current = w;
      }
      workerRef.current.postMessage({ id, wall } satisfies GsRequest);
    }, gsTiming.debounceMs);
    return () => clearTimeout(timer);
  }, [wall, enabled]);

  return state;
}
